import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Card,
    CardContent,
    Box,
    Typography,
    IconButton,
    Chip,
    Stack,
    Collapse,
    useTheme,
    TablePagination,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Divider
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    MoreVert as MoreVertIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import { useResponsiveUtils } from '@utils/responsiveUtils';
import { useAppTheme } from '@contexts/ThemeContext';

export interface ResponsiveTableColumn {
    id: string;
    label: string;
    minWidth?: number;
    align?: 'left' | 'center' | 'right';
    format?: (value: any, row?: any) => React.ReactNode;
    sortable?: boolean;
    filterable?: boolean;
    priority?: 'high' | 'medium' | 'low'; // For hiding columns on smaller screens
    mobileLabel?: string; // Alternative label for mobile
}

export interface ResponsiveTableRow {
    id: string | number;
    [key: string]: any;
}

interface ResponsiveTableProps {
    columns: ResponsiveTableColumn[];
    rows: ResponsiveTableRow[];
    title?: string;
    searchable?: boolean;
    filterable?: boolean;
    paginated?: boolean;
    rowsPerPageOptions?: number[];
    onRowClick?: (row: ResponsiveTableRow) => void;
    actions?: (row: any) => React.ReactNode;
    loading?: boolean;
    emptyMessage?: string;
    stickyHeader?: boolean;
    maxHeight?: number | string;
    customToolbar?: React.ReactNode;
    mobileCardRender?: (row: ResponsiveTableRow, columns: ResponsiveTableColumn[]) => React.ReactNode;
}

export const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
    columns,
    rows,
    title,
    searchable = true,
    filterable = false,
    paginated = true,
    rowsPerPageOptions = [5, 10, 25, 50],
    onRowClick,
    actions,
    loading = false,
    emptyMessage = 'No data available',
    stickyHeader = true,
    maxHeight = 600,
    customToolbar,
    mobileCardRender
}) => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const {
        isMobile,
        isTablet,
        isDesktop,
        isSmallMobile,
        isMediumMobile,
        isSmallTablet,
        getTableResponsive,
        getCardResponsive,
        getTypographyResponsive,
        getButtonResponsive,
        getSpacingResponsive
    } = useResponsiveUtils();

    // State for table functionality
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [filters, setFilters] = useState<{ [key: string]: string }>({});
    const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
    const [viewMode, setViewMode] = useState<'table' | 'card'>('table');

    // Get responsive configurations
    const tableConfig = getTableResponsive();
    const cardConfig = getCardResponsive();
    const h6Typography = getTypographyResponsive('h6');
    const body1Typography = getTypographyResponsive('body1');
    const body2Typography = getTypographyResponsive('body2');
    const captionTypography = getTypographyResponsive('caption');

    // Determine which columns to show based on screen size
    const getVisibleColumns = () => {
        if (isSmallMobile) {
            // Show only high priority columns + first column
            return columns.filter((col, index) =>
                index === 0 || col.priority === 'high'
            ).slice(0, 2); // Maximum 2 columns on very small screens
        } else if (isMediumMobile) {
            // Show high and medium priority columns
            return columns.filter(col =>
                col.priority === 'high' || col.priority === 'medium'
            ).slice(0, 3); // Maximum 3 columns on medium mobile
        } else if (isSmallTablet) {
            // Show all except low priority if too many columns
            return columns.length > 6
                ? columns.filter(col => col.priority !== 'low').slice(0, 5)
                : columns.slice(0, 5);
        } else {
            // Show all columns on larger screens
            return columns;
        }
    };

    const visibleColumns = getVisibleColumns();

    // Filter and search functionality
    const filteredRows = rows.filter(row => {
        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = columns.some(col => {
                const value = row[col.id];
                return value && value.toString().toLowerCase().includes(searchLower);
            });
            if (!matchesSearch) return false;
        }

        // Column filters
        for (const [filterKey, filterValue] of Object.entries(filters)) {
            if (filterValue && row[filterKey] !== filterValue) {
                return false;
            }
        }

        return true;
    });

    // Pagination
    const paginatedRows = paginated
        ? filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : filteredRows;

    // Toggle expanded row
    const toggleExpandedRow = (rowId: string | number) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }
        setExpandedRows(newExpanded);
    };

    // Render mobile card view
    const renderMobileCard = (row: ResponsiveTableRow) => {
        if (mobileCardRender) {
            return mobileCardRender(row, columns);
        }

        const isExpanded = expandedRows.has(row.id);
        const visibleData = visibleColumns.slice(0, 2); // Show first 2 columns in summary
        const hiddenData = columns.filter(col => !visibleColumns.includes(col));

        return (
            <Card
                key={row.id}
                sx={{
                    mb: getSpacingResponsive(8),
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                    borderRadius: cardConfig.borderRadius,
                    '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows[4],
                        transition: 'all 0.2s ease-in-out'
                    },
                    cursor: onRowClick ? 'pointer' : 'default'
                }}
                onClick={() => onRowClick && onRowClick(row)}
            >
                <CardContent sx={{ p: cardConfig.padding, '&:last-child': { pb: cardConfig.padding } }}>
                    {/* Main content - always visible */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {visibleData.map((column, index) => (
                                <Box key={column.id} sx={{ mb: index === 0 ? 1 : 0.5 }}>
                                    {index === 0 ? (
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                ...h6Typography,
                                                fontWeight: 600,
                                                color: theme.palette.text.primary,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {column.format ? column.format(row[column.id], row) : row[column.id]}
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    ...captionTypography,
                                                    color: theme.palette.text.secondary,
                                                    minWidth: 'fit-content'
                                                }}
                                            >
                                                {column.mobileLabel || column.label}:
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    ...body2Typography,
                                                    color: theme.palette.text.primary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {column.format ? column.format(row[column.id], row) : row[column.id]}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Actions and expand button */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            {actions && (
                                <Box onClick={(e) => e.stopPropagation()}>
                                    {actions(row)}
                                </Box>
                            )}
                            {hiddenData.length > 0 && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpandedRow(row.id);
                                    }}
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        p: getSpacingResponsive(4)
                                    }}
                                >
                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                    {/* Expanded content */}
                    {hiddenData.length > 0 && (
                        <Collapse in={isExpanded}>
                            <Divider sx={{ my: 1, borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 }}>
                                {hiddenData.map((column) => (
                                    <Box key={column.id}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                ...captionTypography,
                                                color: theme.palette.text.secondary,
                                                display: 'block',
                                                mb: 0.25
                                            }}
                                        >
                                            {column.mobileLabel || column.label}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                ...body2Typography,
                                                color: theme.palette.text.primary,
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {column.format ? column.format(row[column.id]) : row[column.id] || '-'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    )}
                </CardContent>
            </Card>
        );
    };

    // Render desktop table view
    const renderDesktopTable = () => (
        <TableContainer
            component={Paper}
            sx={{
                maxHeight,
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                '&::-webkit-scrollbar': {
                    width: 8,
                    height: 8,
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                    borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                    borderRadius: 4,
                    '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : theme.palette.text.secondary,
                    },
                },
            }}
        >
            <Table
                stickyHeader={stickyHeader}
                sx={{
                    minWidth: tableConfig.minWidth,
                    '& .MuiTableCell-root': {
                        fontSize: tableConfig.fontSize,
                        padding: tableConfig.cellPadding,
                        borderBottom: isDarkMode
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <TableHead>
                    <TableRow
                        sx={{
                            height: tableConfig.headerHeight,
                            '& .MuiTableCell-head': {
                                backgroundColor: isDarkMode
                                    ? '#1976d2'
                                    : '#1976d2',
                                fontWeight: 600,
                                color: '#ffffff',
                                borderBottom: isDarkMode
                                    ? '2px solid rgba(255, 255, 255, 0.2)'
                                    : '2px solid rgba(0, 0, 0, 0.2)',
                            }
                        }}
                    >
                        {visibleColumns.map((column) => (
                            <TableCell
                                key={column.id}
                                align={column.align || 'left'}
                                style={{ minWidth: column.minWidth }}
                            >
                                {column.label}
                            </TableCell>
                        ))}
                        {actions && (
                            <TableCell align="center" style={{ minWidth: 100 }}>
                                Actions
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedRows.map((row) => (
                        <TableRow
                            key={row.id}
                            hover
                            onClick={() => onRowClick && onRowClick(row)}
                            sx={{
                                height: tableConfig.rowHeight,
                                cursor: onRowClick ? 'pointer' : 'default',
                                '&:hover': {
                                    backgroundColor: isDarkMode
                                        ? 'rgba(255, 255, 255, 0.05)'
                                        : theme.palette.action.hover,
                                },
                                '& .MuiTableCell-body': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            {visibleColumns.map((column) => (
                                <TableCell key={column.id} align={column.align || 'left'}>
                                    {column.format ? column.format(row[column.id], row) : row[column.id]}
                                </TableCell>
                            ))}
                            {actions && (
                                <TableCell align="center">
                                    <Box onClick={(e) => e.stopPropagation()}>
                                        {actions(row)}
                                    </Box>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box sx={{ width: '100%' }}>
            {/* Title and Toolbar */}
            {(title || searchable || filterable || customToolbar) && (
                <Box sx={{
                    mb: getSpacingResponsive(16),
                    p: getSpacingResponsive(16),
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                    borderRadius: cardConfig.borderRadius,
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                    {title && (
                        <Typography
                            variant="h5"
                            sx={{
                                ...getTypographyResponsive('h5'),
                                mb: searchable || filterable ? 2 : 0,
                                color: theme.palette.text.primary,
                                fontWeight: 600
                            }}
                        >
                            {title}
                        </Typography>
                    )}

                    {/* Search and Filters */}
                    {(searchable || filterable) && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: getSpacingResponsive(8),
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between'
                        }}>
                            {searchable && (
                                <TextField
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        minWidth: { xs: '100%', sm: 250 },
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                                            '& fieldset': {
                                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.divider,
                                            },
                                        },
                                    }}
                                />
                            )}

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {/* View mode toggle for mobile */}
                                {isMobile && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={viewMode === 'table' ? <ViewModuleIcon /> : <ViewListIcon />}
                                        onClick={() => setViewMode(viewMode === 'table' ? 'card' : 'table')}
                                        sx={{
                                            ...getButtonResponsive('small'),
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.divider,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        {viewMode === 'table' ? 'Cards' : 'Table'}
                                    </Button>
                                )}

                                {customToolbar}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* Table Content */}
            {loading ? (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                    borderRadius: cardConfig.borderRadius
                }}>
                    <Typography sx={{ color: theme.palette.text.secondary }}>Loading...</Typography>
                </Box>
            ) : filteredRows.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                    borderRadius: cardConfig.borderRadius,
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                    <Typography sx={{ color: theme.palette.text.secondary }}>{emptyMessage}</Typography>
                </Box>
            ) : (
                <>
                    {/* Render based on device and view mode */}
                    {isMobile && viewMode === 'card' ? (
                        <Box>{paginatedRows.map(renderMobileCard)}</Box>
                    ) : (
                        renderDesktopTable()
                    )}
                </>
            )}

            {/* Pagination */}
            {paginated && filteredRows.length > 0 && (
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    sx={{
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                        borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                        color: theme.palette.text.primary,
                        '& .MuiTablePagination-toolbar': {
                            fontSize: body2Typography.fontSize,
                            padding: getSpacingResponsive(8),
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            color: theme.palette.text.primary,
                            fontSize: body2Typography.fontSize,
                        },
                        '& .MuiIconButton-root': {
                            color: theme.palette.text.primary,
                            padding: getSpacingResponsive(8),
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default ResponsiveTable;